import { getCSRFToken } from '@cartwheel/api'
import type { ClinicalQualityScore } from '@cartwheel/api/v1'
import { useCallback, useEffect, useRef, useState } from 'react'

type Status = 'idle' | 'submitting' | 'polling' | 'done' | 'error'

interface Props {
  chartNoteId: string
  clientId: string
  autoRun?: boolean
  resume?: boolean
  onRunStarted?: () => void
  onStatusChange?: (status: Status) => void
  onComplete?: (score: ClinicalQualityScore) => void
  onError?: (message: string) => void
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 10 * 60 * 1000

export function ClinicalQualityCheckerButton({
  chartNoteId,
  clientId,
  autoRun = false,
  resume = false,
  onRunStarted,
  onStatusChange,
  onComplete,
  onError,
}: Props) {
  const [status, setStatus] = useState<Status>('idle')
  const pollStartedAt = useRef(0)
  const pollTimer = useRef<ReturnType<typeof setTimeout>>()

  const updateStatus = useCallback(
    (s: Status, message?: string) => {
      setStatus(s)
      onStatusChange?.(s)
      if (s === 'error' && message) {
        onError?.(message)
      }
    },
    [onStatusChange, onError]
  )

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current)
      pollTimer.current = undefined
    }
  }, [])

  const poll = useCallback(async () => {
    if (Date.now() - pollStartedAt.current >= POLL_TIMEOUT_MS) {
      updateStatus('error', 'Scoring timed out. Please try again.')
      return
    }

    try {
      const url =
        `/api/v1/chart_notes/${chartNoteId}/clinical_quality_scores/poll` +
        `?client_id=${encodeURIComponent(clientId)}`
      const resp = await fetch(url, { headers: { 'X-CSRF-Token': getCSRFToken() } })

      if (!resp.ok) {
        updateStatus('error', 'Scoring failed. Please try again.')
        return
      }

      const data = await resp.json()

      if (data.status === 'finished' && data.clinical_quality_score) {
        updateStatus('done')
        onComplete?.(data.clinical_quality_score)
        return
      }

      if (data.status === 'finished') {
        updateStatus('error', 'Scoring failed. Please try again.')
        return
      }

      if (data.status === 'idle') {
        updateStatus('done')
        return
      }

      pollTimer.current = setTimeout(() => poll(), POLL_INTERVAL_MS)
    } catch {
      updateStatus('error', 'Network error while scoring. Please try again.')
    }
  }, [chartNoteId, clientId, updateStatus, onComplete])

  const runCheck = useCallback(async () => {
    updateStatus('submitting')
    pollStartedAt.current = Date.now()
    onRunStarted?.()

    try {
      const url =
        `/api/v1/chart_notes/${chartNoteId}/clinical_quality_scores` +
        `?client_id=${encodeURIComponent(clientId)}`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
      })

      if (!resp.ok) {
        updateStatus('error', 'Failed to start clinical quality check. Please try again.')
        return
      }

      await resp.json()
      updateStatus('polling')
      pollTimer.current = setTimeout(() => poll(), POLL_INTERVAL_MS)
    } catch {
      updateStatus('error', 'Network error starting clinical quality check.')
    }
  }, [chartNoteId, clientId, onRunStarted, poll, updateStatus])

  const resumePolling = useCallback(() => {
    pollStartedAt.current = Date.now()
    updateStatus('polling')
    void poll()
  }, [poll, updateStatus])

  useEffect(() => {
    if (autoRun && status === 'idle') {
      void runCheck()
    }
  }, [autoRun, status, runCheck])

  useEffect(() => {
    if (resume && !autoRun && status === 'idle') {
      resumePolling()
    }
  }, [resume, autoRun, status, resumePolling])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  return null
}
